import { useRef } from 'react';
import { isStillPlaying } from '../../functions';
import { useWheelScrollsSideways } from '../../hooks';
import { IChatMessage } from '../../Models/LobbyModels';
import { IVisibleOpponent, IVisiblePlayer } from '../../Models/StateModels';
import Opponent from './Opponent';

export interface IDisplayedOpponent {
  opponent: IVisibleOpponent;
  lastSentMessage: IChatMessage | undefined;
}

export interface IOpponentsProps {
  opponents: IDisplayedOpponent[];
  player: IVisiblePlayer;
}

type TurnOrderPlayer = Pick<
  IVisiblePlayer,
  'playerIndex' | 'isTurn' | 'place' | 'forfeited'
>;

function nextPlayerIndex(players: TurnOrderPlayer[]) {
  const playing = players.find((p) => p.isTurn);
  if (!playing) {
    return undefined;
  }
  for (let step = 1; step < players.length; step++) {
    const playerIndex = (playing.playerIndex + step) % players.length;
    const player = players.find((p) => p.playerIndex === playerIndex);
    if (player && isStillPlaying(player)) {
      return playerIndex;
    }
  }
  return undefined;
}

export default function Opponents({ opponents, player }: IOpponentsProps) {
  const stripRef = useRef<HTMLElement>(null);
  useWheelScrollsSideways(stripRef);

  const playerCount = opponents.length + 1;
  const turnOrder = (opponent: IVisibleOpponent) =>
    (opponent.playerIndex - player.playerIndex - 1 + playerCount) %
    playerCount;
  const sortedOpponents = [...opponents].sort(
    (a, b) => turnOrder(a.opponent) - turnOrder(b.opponent)
  );
  const nextIndex = nextPlayerIndex([
    player,
    ...opponents.map((o) => o.opponent),
  ]);

  return (
    <section
      ref={stripRef}
      aria-label="Opponents in turn order"
      className="shrink-0 overflow-x-auto -mb-[14px] pt-[16px] pb-[18px] [scrollbar-width:none]"
    >
      <ol className="mx-auto flex w-max gap-[8px] px-[12px]">
        {sortedOpponents.map(({ opponent, lastSentMessage }) => (
          <Opponent
            key={opponent.playerIndex}
            opponent={opponent}
            isNext={opponent.playerIndex === nextIndex}
            lastSentMessage={lastSentMessage}
          />
        ))}
      </ol>
    </section>
  );
}
